/**
 * Lucky War dashboard API client.
 *
 * All endpoints return JSON from the same-origin PocketBase server at
 * /api/dashboard/..., served from the same origin as the SPA (no CORS).
 *
 * The auth cookie (`lw_auth`) is httpOnly — the SPA never reads it. The
 * server uses it to authorize dashboard endpoints. The 401/403 interceptor
 * below drives auth-state transitions when mid-session auth failures occur.
 */

// ── Shared / reference types ────────────────────────────────────────────────

export interface RegionRef {
  name_th: string;
}

export interface BrandRef {
  name_th: string;
  code: string;
}

export type MetricWindowPreset = 7 | 14 | 30 | 90;

/** Shared query contract for review-driven metric windows. */
export interface MetricWindowParams {
  days?: MetricWindowPreset;
  from?: string;
  to?: string;
}

/** Effective ICT window returned by window-aware dashboard endpoints. */
export interface MetricWindow {
  days: number;
  from: string;
  to: string;
  effective: boolean;
}

// ── Historical range contract (docs/plans/HISTORICAL-DASHBOARD.md) ──────────

export type HistoricalRangeMode = 'day' | 'week' | 'month' | 'custom';
export type HistoricalBucket = 'day' | 'week' | 'month';
export type HistoricalBrandCode = 'lucky_suki' | 'lucky_bbq';

/** Canonical inclusive ICT query fields for the /historical endpoints. */
export interface HistoricalRangeParams {
  mode: HistoricalRangeMode;
  from: string;
  to: string;
  bucket: HistoricalBucket;
}

/** Validated historical range metadata returned by historical endpoints. */
export interface HistoricalRange extends HistoricalRangeParams {
  /** Nominal inclusive date span, including the unobserved tail of an ongoing period. */
  days: number;
  /** The last ICT date that may be queried, clipped only for an ongoing week/month. */
  effective_to: string;
  range_partial: boolean;
  bucket_partial: boolean;
}

export type HistoricalValidationCode =
  | 'invalid_mode'
  | 'invalid_date'
  | 'invalid_range'
  | 'invalid_bucket'
  | 'invalid_brand'
  | 'future_range';

export interface HistoricalFilters {
  brand?: HistoricalBrandCode;
  region?: string;
  branch?: string;
}

export interface HistoricalQuery extends HistoricalRangeParams, HistoricalFilters {}

/** Shared source/data coverage states; range and bucket partiality are separate. */
export type HistoricalCoverageState =
  | 'available'
  | 'complete'
  | 'partial'
  | 'no_data'
  | 'pending'
  | 'unavailable'
  | 'not_comparable';

export type HistoricalFreshnessState = 'fresh' | 'stale' | 'unknown';

/**
 * Missing metrics are null; a reported zero remains an observed value. Expected
 * dates are nominal calendar dates, while complete dates cover every in-scope
 * active branch and branch-day counts count distinct resolved cells.
 */
export interface HistoricalCoverage {
  state: HistoricalCoverageState;
  freshness: HistoricalFreshnessState;
  expected_dates: number;
  observed_dates: number;
  complete_dates: number;
  reporting_branch_days: number;
  expected_branch_days: number;
  data_through: string | null;
  updated_at: string | null;
  provenance: string | null;
}

export interface HistoricalSourceCoverage extends HistoricalCoverage {
  source: string;
}

export type HistoricalCoverageBySource = Record<string, HistoricalSourceCoverage>;

/** Fixed Monday–Sunday coverage fields used by every weekday profile row. */
export interface HistoricalWeekdayCoverage {
  expected_dates: number;
  observed_dates: number;
  complete_dates: number;
  reporting_branch_days: number;
  expected_branch_days: number;
}

export interface HistoricalWeekdayProfileRow extends HistoricalWeekdayCoverage {
  /** 1 = Monday, ..., 7 = Sunday. */
  weekday: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  label_th: string;
  sales_total: number | null;
  sales_average: number | null;
}

/** Metric-neutral series fields shared by later historical read models. */
export interface HistoricalSeriesPoint {
  from: string;
  to: string;
  has_data: boolean;
  coverage: HistoricalCoverage;
}

/** V1 comparisons are deliberately disabled and must serialize as null. */
export interface HistoricalResponseMeta {
  range: HistoricalRange;
  as_of: string;
  comparison: null;
}

export interface HistoricalSalesMetric {
  net_total: number | null;
  gross_sales: number | null;
  discount: number | null;
  bills: number | null;
  customers: number | null;
  night_customers: number | null;
  promo_cost_ratio_pct: number | null;
}

export interface HistoricalReviewSummary {
  raw_count: number;
  volume: number | null;
  analyzed: number;
  negative: number;
  negative_pct: number | null;
  rating_from_collected_reviews: number | null;
  rating_sample: number;
}

export interface HistoricalPromotionSummary {
  registered_overlaps: number;
  observed_activity: number;
  ambiguous_activity: number;
}

export interface HistoricalSalesSummary {
  sales: HistoricalSalesMetric;
  reviews: HistoricalReviewSummary;
  promotions: HistoricalPromotionSummary;
  unattributed_net_total: number | null;
  coverage: HistoricalCoverage;
}

export interface HistoricalSalesSeries extends HistoricalSalesMetric {
  from: string;
  to: string;
  natural_from: string;
  natural_to: string;
  partial: boolean;
  has_data: boolean;
  unattributed_net_total: number | null;
  coverage: HistoricalCoverage;
}

export interface HistoricalRegion {
  id: string;
  name_th: string;
  branch_count: number;
  branches_reporting: number;
  review_volume: number | null;
  analyzed_reviews: number;
  negative_reviews: number;
  negative_pct: number | null;
  rating_from_collected_reviews: number | null;
  rating_sample: number;
  sales: HistoricalSalesMetric;
  coverage: HistoricalCoverage;
}

export interface HistoricalPromotionActivity {
  state: HistoricalCoverageState;
  item_rows: number;
  voucher_rows: number;
  units: number | null;
  revenue: number | null;
  item_discount: number | null;
  voucher_discount: number | null;
  ambiguous: boolean;
  ambiguous_units: number;
  ambiguous_voucher_discount: number;
}

export interface HistoricalPromotion {
  id: string;
  name_th: string;
  status: string;
  start_date: string;
  end_date: string;
  price_adult: number;
  price_child: number;
  overlap_start: string;
  overlap_end: string;
  overlap_type: string;
  brand_scope: string[];
  branch_scope: string[];
  item_codes: string[];
  vc_codes: string[];
  observed_activity: HistoricalPromotionActivity;
  campaign_lift_pct: number | null;
  campaign_metrics?: Record<string, unknown>;
}

export interface HistoricalResponse extends HistoricalResponseMeta {
  filters: HistoricalFilters;
  summary: HistoricalSalesSummary;
  series: { sales: HistoricalSalesSeries[] };
  weekday_profile: HistoricalWeekdayProfileRow[];
  regions: HistoricalRegion[];
  promotions: HistoricalPromotion[];
  coverage: HistoricalCoverageBySource;
  honesty_notes: string[];
}

export interface HistoricalBranch extends HistoricalSalesMetric {
  id: string;
  name_th: string;
  region_id: string;
  region_name_th: string;
  brand_id: string;
  brand_code: string;
  brand_name_th: string;
  google_rating: number | null;
  google_rating_label: string;
  google_rating_at: string | null;
  google_review_count: number | null;
  review_volume: number | null;
  rating_from_collected_reviews: number | null;
  rating_sample: number;
  analyzed_reviews: number;
  negative_reviews: number;
  negative_pct: number | null;
  days_covered: number;
  buckets_covered: number;
  coverage: HistoricalCoverage;
}

export interface HistoricalBranchesResponse {
  range: HistoricalRange;
  as_of: string;
  comparison: null;
  filters: HistoricalFilters;
  items: HistoricalBranch[];
  page: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
  coverage: HistoricalCoverage;
}

export interface HistoricalVoiceMetrics {
  raw_count: number;
  analyzed_count: number;
  negative_count: number;
  negative_pct: number | null;
  sample_state: string;
  attributed_count?: number;
  unmatched_count?: number;
  unmatched_pct?: number | null;
}

export interface HistoricalVoiceSummary {
  reviews: HistoricalVoiceMetrics & {
    by_source: Record<string, HistoricalVoiceMetrics>;
  };
  facebook: HistoricalVoiceMetrics;
}

export interface HistoricalVoiceEvidenceItem extends DiscoveryVoiceEvidenceItem {
  incident_id?: string | null;
  incident_type?: string | null;
  incident_reason?: string | null;
  incident_confidence?: number | null;
}

export interface HistoricalVoiceEvidence {
  items: HistoricalVoiceEvidenceItem[];
  next_cursor: string | null;
  denominators: DiscoveryVoiceDenominators;
}

export interface HistoricalVoiceTopicBreakdown {
  source: string;
  topic: string;
  topic_label_th: string;
  mention_count: number;
  analyzed_denominator: number;
  negative_count: number;
  negative_pct: number | null;
  sample_state: string;
}

export interface HistoricalVoiceBranchBreakdown {
  source: string;
  branch_id: string;
  branch_name: string;
  region_name: string;
  brand_code: string;
  raw_count: number;
  analyzed_count: number;
  negative_count: number;
  negative_pct: number | null;
  sample_state: string;
  attribution_scope?: string;
}

export interface HistoricalVoiceIncident extends DiscoveryVoiceIncident {
  state: string;
}

export interface HistoricalVoiceSourceCoverage {
  source: string;
  state: HistoricalCoverageState;
  freshness: HistoricalFreshnessState;
  raw_count: number;
  analyzed_count: number;
  negative_count: number;
  negative_pct: number | null;
  sample_state: string;
  expected_dates: number;
  observed_dates: number;
  complete_dates: number;
  reporting_branch_days: number;
  expected_branch_days: number;
  data_through: string | null;
  updated_at: string | null;
  provenance: string | null;
}

export interface HistoricalVoiceResponse {
  range: HistoricalRange;
  as_of: string;
  comparison: null;
  filters: {
    brand: BrandRef | null;
    region: string;
    branch: string;
    topic: string;
    source: string;
  };
  signals_to_check: DiscoveryVoiceSignal[];
  summary: HistoricalVoiceSummary;
  customer_voice_evidence: HistoricalVoiceEvidence;
  denominators: DiscoveryVoiceDenominators;
  topic_breakdown: HistoricalVoiceTopicBreakdown[];
  branch_breakdown: HistoricalVoiceBranchBreakdown[];
  incidents: HistoricalVoiceIncident[];
  freshness: DiscoveryVoiceFreshness;
  coverage: Record<string, HistoricalVoiceSourceCoverage>;
}

export interface HistoricalSocialMetricCoverage {
  status: HistoricalCoverageState;
  expected_posts: number;
  snapshot_covered_posts: number;
  eligible_posts: number;
  missing_snapshot_posts: number;
  missing_value_posts: number;
  observation_dates: string[];
}

export interface HistoricalSocialCohortCoverage {
  status: HistoricalCoverageState;
  expected_posts: number;
  mature_posts: number;
  pending_posts: number;
  snapshot_covered_posts: number;
  missing_snapshot_posts: number;
}

export interface HistoricalSocialPublished {
  status: HistoricalCoverageState;
  post_count: number;
  mature_post_count: number;
  pending_post_count: number;
  missing_snapshot_post_count: number;
  publication_dates: string[];
  observation_dates: string[];
  reach: number | null;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  clicks: number | null;
  engagement_rate_pct: number | null;
  reach_semantics: string;
  reach_label_th: string;
  metric_coverage: Record<string, HistoricalSocialMetricCoverage>;
  coverage: HistoricalSocialCohortCoverage;
  unattributed_post_count: number;
  excluded_by_brand_count: number;
  truncated: boolean;
}

export interface HistoricalSocialAccrued {
  status: HistoricalCoverageState;
  reason: string;
  post_count: number;
  reach: number | null;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  clicks: number | null;
  engagement_rate_pct: number | null;
}

export interface HistoricalSocialCapability {
  status: HistoricalCoverageState;
  reason?: string;
  live_path?: string;
}

export interface HistoricalSocialResponse {
  generated_at: string;
  range: HistoricalRange;
  as_of: string;
  comparison: null;
  brand: string;
  published_in_range: HistoricalSocialPublished;
  engagement_accrued_in_range: HistoricalSocialAccrued;
  capability_matrix: {
    facebook: {
      published_in_range: HistoricalSocialCapability;
      engagement_accrued_in_range: HistoricalSocialCapability;
    };
    instagram: HistoricalSocialCapability;
    tiktok: HistoricalSocialCapability;
  };
  honesty_notes: string[];
}

// Customer-unit split from buffet item_facts. These are POS item quantities,
// not a headcount; the backend omits the whole block when item_facts has no
// coverage for the requested window.
export interface CustSplit {
  window_start: string;
  window_end: string;
  adult_units: number;
  kid_units: number;
  prev_adult_units?: number;
  prev_kid_units?: number;
  adult_delta_pct?: number;
  kid_delta_pct?: number;
  comparison_available: boolean;
  matched_days?: number;
}

// ── FB Feedback (P4.FB.2, bead lucky-war-6lr.1.7.5) ────────────────────────

export interface FbEvidenceLink {
  kind: string;
  url: string;
  count: number;
  snippet: string; // message trimmed ~60 chars
  topic: string; // primary topic (first of the taxonomy array)
  sentiment: string; // negative | neutral | positive
  branch_name: string | null; // null when no branch resolved
  created_at: string; // RFC3339; rendered as relative time
}

export interface FbBrandFeedback {
  comment_count: number;
  negative_pct: number | null;
  top_topics: string[];
  evidence: FbEvidenceLink[];
  evidence_all: FbEvidenceLink[];
  unmatched_pct: number | null;
  unmatched_count: number;
  total_count: number;
  last_updated_at: string | null;
}

export interface FbBranchFeedback {
  comment_count: number;
  negative_pct: number | null;
  top_topics: string[];
  evidence: FbEvidenceLink[];
  evidence_all: FbEvidenceLink[];
  chain_unmatched_pct: number | null;
  chain_unmatched_count: number;
  chain_total_count: number;
}

// ── Overview ─────────────────────────────────────────────────────────────────

export interface RegionSalesWow {
  current: number;               // current data-week sales (฿)
  delta_pct: number;             // matched-day WoW %; 0 when gated
  comparison_available: boolean; // false → UI shows "—" (never a fake 0%)
}

export interface RegionSummary {
  region_id: string;
  region_name: string;
  branch_count: number;
  rating_avg: number | null;
  review_count: number;
  sales_net_total: number | null;
  sales_wow?: RegionSalesWow | null;   // weekly sales + matched-day WoW (etcn.3)
  negative_pct?: number | null;        // 30-day, gated on min enriched sample
  enriched_count?: number;             // sample size behind negative_pct
  branches_reporting?: number;         // branches with ≥1 sales row this week
}

export interface BrandSummary {
  brand_id: string;
  code: string;
  brand_name: string;
  branch_count: number;
  rating_avg: number | null;
  review_count: number;
}

export interface DataFreshness {
  last_success_at: string | null;
  stale_count: number;
}

export interface SourceFreshness {
  source_id: string;
  last_success_at: string | null;
  stale_count: number;
  listing_count: number;
}

export interface NegativePctTrendItem {
  month: string;
  negative_pct: number | null;
  review_count: number;
}

export interface IdentityQueue {
  pending_count: number;
  total_reviews: number;
  unmatched_pct: number;
  oldest_pending_at: string | null;
}

export interface OverviewTopComplaintTopic {
  topic: string;                        // taxonomy key, e.g. "cleanliness"
  topic_label: string;                  // Thai display name
  neg_count: number;                    // negative reviews mentioning this topic (30d)
  total_neg_count: number;              // all negative reviews (30d)
  share_of_negatives: number;           // neg_count / total_neg_count * 100
  is_systemic: boolean;                 // share > 35%
  prior_share_of_negatives: number | null; // previous 30-day window; null if no prior data
  trend: 'up' | 'down' | 'flat' | 'new';
  review_count_30d: number;             // total reviews in window (denominator for negative %)
  is_seafood_freshness: boolean;        // shrinkage/spoilage proxy flag
}

export interface OverviewResponse {
  total_branches: number;
  branches_with_data: number;
  chain_rating_avg: number | null;
  total_reviews: number;
  per_region: RegionSummary[];
  per_brand: BrandSummary[];
  data_freshness: DataFreshness;
  rating_trend: RatingTrendItem[];
  per_source_freshness: SourceFreshness[];
  identity_queue: IdentityQueue;
  chain_negative_pct: number | null;
  chain_neg_count_30d: number;       // negative reviews (30d) — calm-state fraction numerator
  chain_review_count_30d: number;    // enriched reviews (30d) — fraction denominator
  negative_pct_trend: NegativePctTrendItem[];
  fb_feedback: FbBrandFeedback | null;
  sales_net_total: number | null;
  sales_bill_count: number | null;
  sales_cust_count: number | null;
  review_volume_delta: WeeklyKpiDelta | null;
  rating_delta: WeeklyKpiDelta | null;
  top_complaint_topic: OverviewTopComplaintTopic | null;
  window?: MetricWindow;
  window_effective?: boolean;
}

// ── Branch list ──────────────────────────────────────────────────────────────

export interface BranchSummary {
  id: string;
  name_th: string;
  address_th: string;
  phone: string | null;
  lat: number | null;
  lng: number | null;
  region: RegionRef;
  brand: BrandRef;
  rating: number | null;
  review_count: number | null;
  reviews_in_db: number;
  latest_review_at: string | null;
  negative_pct: number | null;
  negative_enriched: number | null;
  sales_net_total: number | null;
  health_status: 'ok' | 'degraded' | 'dead';
  last_success_at: string | null;
  mtd_net_total?: number | null;
  dod_delta_pct?: number | null;
  weekend_classification?: 'weekend_heavy' | 'weekday_heavy' | 'balanced' | 'insufficient';
}

/** Sales totals for the requested metric window, when the branch has data. */
export interface WindowSales {
  net_total: number | null;
  bills: number | null;
  customers: number | null;
  days_covered: number;
  prev_net_total: number | null;
  comparison_available: boolean;
  wow_delta_pct: number | null;
}

export interface BranchListResponse {
  items: BranchSummary[];
  page: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
  window?: MetricWindow;
  window_effective?: boolean;
}

export interface BranchListParams extends MetricWindowParams {
  region?: string;
  brand?: string;
  q?: string;
  // bead lucky-war-mee: when set, restricts to branches with ≥1 review
  // carrying this topic in its `topics` JSON array. Server lowercases the
  // value and matches against the lowercase fixed taxonomy.
  topic?: string;
  sort?: 'rating' | 'reviews' | 'name' | 'negative';
  page?: number;
  perPage?: number;
}

// ── Branch detail ────────────────────────────────────────────────────────────

export interface PlaceMeta {
  rating: number | null;
  review_count: number | null;
  categories: string[] | null;
  hours: Record<string, string> | null;
  popular_times: unknown | null; // null in v1
  price_level: string | null;
  status: string | null;
  fetched_at: string | null;
}

export interface ReviewItem {
  id: string;
  author_name: string;
  rating: number | null;
  language: string;
  text: string;
  posted_at: string;
  sentiment: string | null;
  topics: string[] | null;
}

export interface PaginatedReviews {
  items: ReviewItem[];
  page: number;
  perPage: number;
  totalItems: number;
}

export interface RatingDist {
  rating: number;
  count: number;
}

export interface RatingTrendItem {
  month: string;
  avg_rating: number | null;
  review_count: number;
}

export interface BranchDetailResponse {
  id: string;
  name_th: string;
  address_th: string;
  phone: string | null;
  region: RegionRef;
  brand: BrandRef;
  rating: number | null;
  review_count: number | null;
  reviews_in_db: number;
  latest_review_at: string | null;
  negative_pct: number | null;
  review_count_30d: number;
  negative_count_30d: number;
  insufficient_data: boolean;
  is_experimental: boolean;
  health_status: 'ok' | 'degraded' | 'dead';
  last_success_at: string | null;
  rating_source_label: string;
  place_meta: PlaceMeta | null;
  reviews: PaginatedReviews;
  rating_distribution: RatingDist[];
  rating_trend: RatingTrendItem[];
  fb_feedback: FbBranchFeedback | null;
  window?: MetricWindow;
  window_effective?: boolean;
}

// ── Ingest failures ──────────────────────────────────────────────────────────

export interface FailureItem {
  branch_name: string | null;
  listing_label: string | null;
  listing_source: string | null;
  error_class: 'timeout' | 'captcha' | 'place_not_found' | 'parse' | 'http_429';
  error_message: string | null;
  attempts: number;
  started_at: string;
  finished_at: string | null;
  run_started_at: string | null;
}

export interface ErrorClassCount {
  error_class: string;
  count: number;
}

export interface IngestFailuresResponse {
  items: FailureItem[];
  totalItems: number;
  limit: number;
  error_classes: ErrorClassCount[];
}

// ── Fetcher ──────────────────────────────────────────────────────────────────

const BASE = '/api/dashboard';

function buildQuery(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

/**
 * Structured error thrown by the API client. Components can check `code` to
 * handle auth-related errors before they bubble to generic error handlers.
 */
export class ApiError extends Error {
  status: number;
  code: string | null;

  constructor(status: number, message: string, code: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Internal JSON body shape for auth-related error responses from the server.
 * Dashboard endpoints return {error: "unauthorized" | "access_revoked"}.
 */
interface ErrorBody {
  error?: string;
  error_code?: string;
  data?: {
    error_code?: string;
  };
}

function errorCodeFromBody(body: ErrorBody | null): string | null {
  return body?.error || body?.error_code || body?.data?.error_code || null;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { signal });

  if (res.ok) {
    return res.json() as Promise<T>;
  }

  // ── Non-2xx → parse error body ─────────────────────────────────────────────
  let body: ErrorBody | null = null;
  try {
    body = (await res.json()) as ErrorBody;
  } catch {
    // Body is not JSON or empty — surface a generic error without auth checks.
    throw new ApiError(res.status, `ไม่สามารถเชื่อมต่อได้ (${res.status})`, null);
  }

  await handleAuthError(res.status, body);
  throw new ApiError(
    res.status,
    `ไม่สามารถเชื่อมต่อได้ (${res.status})`,
    errorCodeFromBody(body),
  );
}

/**
 * Shared auth-error handling, used by both get() and postMultipart().
 * Drives auth-state transitions via dynamic imports.
 */
async function handleAuthError(status: number, body: ErrorBody | null): Promise<void> {
  // ── Auth interceptor: drive state transitions on auth-related failures ─────
  // These imports are dynamic to avoid circular dependency: api.ts ← useAuth.ts.
  // useAuth.ts never imports api.ts, so this is safe.

  // 401 Unauthorized — expired session or no cookie.
  // Transition to unauthenticated; the router guard redirects to /login.
  if (status === 401 && body?.error === 'unauthorized') {
    import('@/composables/useAuth').then(({ setUnauthenticated }) => {
      setUnauthenticated();
    });
    throw new ApiError(401, 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง', 'unauthorized');
  }

  // 403 Forbidden / access_revoked — admin revoked approval mid-session.
  // Transition to revoked, clear the dead cookie, router guard → /revoked.
  if (status === 403 && body?.error === 'access_revoked') {
    import('@/composables/useAuth').then(async ({ setRevoked, logout }) => {
      setRevoked();
      await logout();
    });
    throw new ApiError(403, 'การเข้าถึงถูกระงับโดยผู้ดูแลระบบ', 'access_revoked');
  }

  // 403 Forbidden / pending_activation — shouldn't happen on dashboard calls
  // once the server gate is correct, but defensive.
  if (status === 403 && body?.error === 'pending_activation') {
    import('@/composables/useAuth').then(({ setPending }) => {
      setPending();
    });
    throw new ApiError(403, 'บัญชียังไม่ได้รับการอนุมัติ', 'pending_activation');
  }
}

/**
 * POST with multipart/form-data. Same auth-error interception as get().
 * For non-200 responses, parses JSON body and drives auth state transitions.
 */
async function postMultipart<T>(path: string, formData: FormData, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    body: formData,
    signal,
  });

  if (res.ok) {
    return res.json() as Promise<T>;
  }

  let body: ErrorBody | null = null;
  try {
    body = (await res.json()) as ErrorBody;
  } catch {
    throw new ApiError(res.status, `ไม่สามารถเชื่อมต่อได้ (${res.status})`, null);
  }

  await handleAuthError(res.status, body);
  throw new ApiError(
    res.status,
    `ไม่สามารถเชื่อมต่อได้ (${res.status})`,
    errorCodeFromBody(body),
  );
}

/**
 * POST with a JSON body. Same auth-error interception as get().
 */
async function postJSON<T>(path: string, payload: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (res.ok) {
    return res.json() as Promise<T>;
  }

  let body: ErrorBody | null = null;
  try {
    body = (await res.json()) as ErrorBody;
  } catch {
    throw new ApiError(res.status, `ไม่สามารถเชื่อมต่อได้ (${res.status})`, null);
  }

  await handleAuthError(res.status, body);
  throw new ApiError(
    res.status,
    `ไม่สามารถเชื่อมต่อได้ (${res.status})`,
    errorCodeFromBody(body),
  );
}

/**
 * PATCH with a JSON body. Same auth-error interception as get().
 */
async function patchJSON<T>(path: string, payload: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (res.ok) {
    return res.json() as Promise<T>;
  }

  let body: ErrorBody | null = null;
  try {
    body = (await res.json()) as ErrorBody;
  } catch {
    throw new ApiError(res.status, `ไม่สามารถเชื่อมต่อได้ (${res.status})`, null);
  }

  await handleAuthError(res.status, body);
  throw new ApiError(
    res.status,
    `ไม่สามารถเชื่อมต่อได้ (${res.status})`,
    errorCodeFromBody(body),
  );
}

// ── Public API functions ─────────────────────────────────────────────────────

function historicalQueryParams(
  range: HistoricalRangeParams,
  filters: HistoricalFilters = {},
): Record<string, string | number | undefined> {
  return {
    mode: range.mode,
    from: range.from,
    to: range.to,
    bucket: range.bucket,
    brand: filters.brand,
    region: filters.region,
    branch: filters.branch,
  };
}

/** Fetches the flexible-period historical summary and sales read model. */
export function fetchHistorical(
  params: HistoricalQuery,
  signal?: AbortSignal,
): Promise<HistoricalResponse> {
  return get<HistoricalResponse>(
    `/historical${buildQuery(historicalQueryParams(params, params))}`,
    signal,
  );
}

export type HistoricalBranchSort = 'sales' | 'bills' | 'customers' | 'negative' | 'reviews' | 'rating' | 'name';

export interface HistoricalBranchesParams extends HistoricalQuery {
  q?: string;
  sort?: HistoricalBranchSort;
  page?: number;
  perPage?: number;
}

/** Fetches the server-paginated historical branch table. */
export function fetchHistoricalBranches(
  params: HistoricalBranchesParams,
  signal?: AbortSignal,
): Promise<HistoricalBranchesResponse> {
  return get<HistoricalBranchesResponse>(
    `/historical/branches${buildQuery({
      ...historicalQueryParams(params, params),
      q: params.q,
      sort: params.sort,
      page: params.page,
      perPage: params.perPage,
    })}`,
    signal,
  );
}

export type HistoricalVoiceSource = 'google_maps' | 'wongnai' | 'facebook';

export interface HistoricalVoiceParams extends HistoricalQuery {
  topic?: string;
  source?: HistoricalVoiceSource;
  cursor?: string;
  perPage?: number;
}

/** Fetches source-separated historical customer voice and evidence. */
export function fetchHistoricalVoice(
  params: HistoricalVoiceParams,
  signal?: AbortSignal,
): Promise<HistoricalVoiceResponse> {
  return get<HistoricalVoiceResponse>(
    `/historical/voice${buildQuery({
      ...historicalQueryParams(params, params),
      topic: params.topic,
      source: params.source,
      cursor: params.cursor,
      perPage: params.perPage,
    })}`,
    signal,
  );
}

/** Fetches the local-only historical Facebook capability panel. */
export function fetchHistoricalSocial(
  params: HistoricalQuery,
  signal?: AbortSignal,
): Promise<HistoricalSocialResponse> {
  return get<HistoricalSocialResponse>(
    `/historical/social${buildQuery(historicalQueryParams(params, params))}`,
    signal,
  );
}

export function fetchOverview(signal?: AbortSignal, brand?: string, window?: MetricWindowParams): Promise<OverviewResponse> {
  return get<OverviewResponse>(`/overview${buildQuery({ brand, ...window })}`, signal);
}

// ── Movers (bead lucky-war-a82y) ────────────────────────────────────────────
// "สาขาที่ต้องลงมือก่อน" — branches whose rating dropped ≥0.3 WoW.

export interface MoverBranch {
  branch_id: string;
  branch_name: string;
  brand_code?: string;           // lucky_suki|lucky_bbq — disambiguates same-mall pairs
  rating_this_week: number | null;
  rating_last_week: number | null;
  rating_delta: number;           // this - last; always <= -threshold
  review_count_7d: number;
  review_count_prev: number;      // previous 7-day window; tap-to-expand driver (bead eizj)
  dominant_negative_topic: string | null;        // taxonomy key, e.g. "cleanliness"
  dominant_negative_topic_label: string | null;  // Thai display name
}

export interface MoversResponse {
  items: MoverBranch[];
  total_branches_checked: number;  // calm-state numerator "50/57 ตรวจแล้ว"
  total_branches: number;          // total active branches — the denominator total
  threshold: number;
  min_reviews: number;
  window_days: number;
  is_experimental: boolean;
  method?: string;
  method_label?: string;
  no_qualifier_message?: string | null;
  cohort_size?: number;
}

export function fetchMovers(signal?: AbortSignal, brand?: string): Promise<MoversResponse> {
  return get<MoversResponse>(`/movers${buildQuery({ brand })}`, signal);
}

// ── Sales anchor (bead lucky-war-v848) ─────────────────────────────────────
// The single big-number sales card: WoW delta + breadth + promo cost sub-line.

export interface SalesAnchorResponse {
  net_total: number;               // current ISO week chain sales (absolute)
  bill_count: number;
  cust_count: number;
  delta_pct: number | null;        // WoW % change; null when gated (no prev week)
  delta_available: boolean;        // true when a same-weekday previous week exists
  prior_net_total?: number | null; // matched-day D-7 baseline (฿); null when gated
  delta_baht?: number | null;      // signed ฿ delta; null when gated
  current_matched_net_total?: number | null; // matched current-week total (฿) — apples-to-apples hero
  matched_days: number;            // weekdays with sales in BOTH weeks (0–7)
  matched_dates?: string[];        // current-week ICT ISO dates with a D-7 pair
  branches_up: number;             // breadth — only meaningful when delta_available
  branches_down: number;
  branches_total: number;          // branches with sales in the current week
  branches_active_total: number;   // total active branches (coverage stamp denominator)
  promo_cost_pct: number | null;   // voucher/gross %, live (correlation, not lift)
  ingested_weeks: number;          // distinct INGESTED ISO weeks (promo-lift readiness meter)
  is_empty: boolean;               // true when no sales data at all
  is_experimental: boolean;
  sales_mix?: SalesMix;
  cust_split?: CustSplit;
}

export function fetchSalesAnchor(signal?: AbortSignal, brand?: string): Promise<SalesAnchorResponse> {
  return get<SalesAnchorResponse>(`/sales/anchor${buildQuery({ brand })}`, signal);
}

// ── Sales WoW waterfall (bead lucky-war-c0r8) ───────────────────────────────
// WoW รายสาขาจะเปิดเมื่อมีข้อมูลครบ 2 สัปดาห์เต็มแบบจันทร์–อาทิตย์ในเวลา ICT
// ก่อนถึง gate backend จะส่ง reason ภาษาไทยเพื่อให้ UI แสดงสถานะประมาณการแทน
export interface SalesWoWBranch {
  branch_id: string;
  branch_name_th: string;
  current_week_total: number;
  prior_week_total: number;
  delta: number;
  delta_pct: number | null;
}

export interface SalesWoWRollup {
  current_week_total: number;
  prior_week_total: number;
  delta: number;
  delta_pct: number | null;
}

export interface SalesWoWCoverage {
  weeks_compared: number;
  current_week_start: string;
  current_week_end: string;
  prior_week_start: string;
  prior_week_end: string;
  is_complete: boolean;
  days_with_data: number;
}

export interface SalesWoWResponse {
  branches: SalesWoWBranch[];
  rollup: SalesWoWRollup;
  coverage: SalesWoWCoverage;
  is_experimental: boolean;
  is_empty: boolean;
  gate_not_met: boolean;
  reason: string;
  brand?: BrandRef;
}

export function fetchSalesWoW(signal?: AbortSignal, brand?: string): Promise<SalesWoWResponse> {
  return get<SalesWoWResponse>(`/sales/wow${buildQuery({ brand })}`, signal);
}

export interface SalesMix {
  suki_net_total: number;
  bbq_net_total: number;
  bbq_pct_of_attributed_sales: number;
  unattributed_net_total: number;
}

export function fetchBranches(params: BranchListParams, signal?: AbortSignal): Promise<BranchListResponse> {
  return get<BranchListResponse>(`/branches${buildQuery(params as Record<string, string | number | undefined>)}`, signal);
}

export interface BranchDetailParams extends MetricWindowParams {
  reviewPage?: number;
  reviewPerPage?: number;
  sentiment?: 'negative' | 'positive' | 'neutral';
  topic?: string;
}

export function fetchBranchDetail(id: string, signal?: AbortSignal, params?: BranchDetailParams): Promise<BranchDetailResponse> {
  const qs = params ? buildQuery(params as Record<string, string | number | undefined>) : '';
  return get<BranchDetailResponse>(`/branches/${encodeURIComponent(id)}${qs}`, signal);
}

// ── Per-branch sales history ────────────────────────────────────────────────

export interface BranchSalesTrendPoint {
  date: string;
  net_total: number | null;
  bill_count: number | null;
  cust_count: number | null;
  baseline: number | null;
  /** Same-brand region mean for this date (daily view; null on monthly). */
  region_avg: number | null;
  /** Same-brand countrywide mean for this date (daily view; null on monthly). */
  country_avg: number | null;
  /** Monthly view: region mean summed over this branch's data days. */
  region_total: number | null;
  /** Monthly view: countrywide mean summed over this branch's data days. */
  country_total: number | null;
  missing: boolean;
}

export interface BranchSalesTrendCoverage {
  first_date: string | null;
  last_date: string | null;
  days_with_data: number;
  baseline_samples: number;
  baseline_ready: boolean;
}

// Same-weekday self-benchmark (store manager comparing the branch with itself).
export interface BranchSalesWeekdayCompareDay {
  date: string;
  net_total: number | null;
  bill_count: number | null;
  cust_count: number | null;
}

export interface BranchSalesWeekdayCompare {
  current: BranchSalesWeekdayCompareDay | null;
  previous: BranchSalesWeekdayCompareDay | null;
  weeks_back: number;
  delta_net_pct: number | null;
  delta_bill_pct: number | null;
  delta_cust_pct: number | null;
}

export interface BranchSalesTrendResponse {
  branch_id: string;
  view: 'daily' | 'monthly';
  points: BranchSalesTrendPoint[];
  coverage: BranchSalesTrendCoverage;
  as_of: string | null;
  data_through: string | null;
  weekday_compare?: BranchSalesWeekdayCompare | null;
}

export function fetchBranchSalesTrend(
  branchId: string,
  view: 'daily' | 'monthly' = 'daily',
  signal?: AbortSignal,
): Promise<BranchSalesTrendResponse> {
  return get<BranchSalesTrendResponse>(
    `/branches/${encodeURIComponent(branchId)}/sales-trend${buildQuery({ view })}`,
    signal,
  );
}

export interface BranchNextAction {
  kind: 'item_attach' | 'negative_pct' | 'rating';
  th_text: string;
  metric_value: number;
  peer_mean: number;
  z_score: number;
  item_code?: string;
  item_name_th?: string;
  window_start: string;
  window_end: string;
  peers_compared: number;
}

export interface BranchNextActionResponse {
  action: BranchNextAction | null;
  data_coverage: {
    window_start: string;
    window_end: string;
    item_days_with_data: number;
    metric_days_with_data: number;
    peers_available: number;
  };
}

export function fetchBranchNextAction(branchId: string, signal?: AbortSignal): Promise<BranchNextActionResponse> {
  return get<BranchNextActionResponse>(`/branches/${encodeURIComponent(branchId)}/next-action`, signal);
}

export function fetchIngestFailures(limit?: number, signal?: AbortSignal): Promise<IngestFailuresResponse> {
  const qs = limit != null ? `?limit=${Math.max(1, Math.min(200, limit))}` : '';
  return get<IngestFailuresResponse>(`/ingest/failures${qs}`, signal);
}

// ── Ingest runs timeline + liveness (jjn.9.1 / jjn.9.2) ───────────────────────

export interface IngestRun {
  id: string;
  source_id: string;
  scope: string;
  status: 'running' | 'succeeded' | 'failed' | 'partial' | 'queued';
  started_at: string;
  finished_at: string | null;
  duration_seconds: number | null;
  jobs_total: number;
  jobs_succeeded: number;
  jobs_failed: number;
  reviews_new: number;
  trigger: string;
  error_summary: string | null;
}

export interface IngestRunsResponse {
  items: IngestRun[];
  limit: number;
}

export function fetchIngestRuns(limit?: number, signal?: AbortSignal): Promise<IngestRunsResponse> {
  const qs = limit != null ? `?limit=${Math.max(1, Math.min(100, limit))}` : '';
  return get<IngestRunsResponse>(`/ingest/runs${qs}`, signal);
}

export interface SourceLiveness {
  source_id: string;
  last_ok_at: string | null;
  age_seconds: number | null;
  has_running: boolean;
  stale_running_count: number;
}

export interface IngestLivenessResponse {
  sources: SourceLiveness[];
  checked_at: string;
}

export function fetchIngestLiveness(signal?: AbortSignal): Promise<IngestLivenessResponse> {
  return get<IngestLivenessResponse>('/ingest/liveness', signal);
}

// Viewer-gated alerts (jjn.9.3) — same shape as the admin alerts response.
export function fetchIngestAlerts(signal?: AbortSignal): Promise<AdminAlertsResponse> {
  return get<AdminAlertsResponse>('/ingest/alerts', signal);
}

// ── Topic data model (used by brand-split helpers) ───────────────────────────

export interface TopicAffectedBranch {
  branch_id: string;
  branch_name: string;
  brand_code?: string;           // lucky_suki|lucky_bbq — disambiguates same-mall pairs
  neg_count: number;
  neg_share_pct: number;
}

export interface TopicExampleSnippet {
  review_id: string;
  snippet: string;
  branch_name: string;
  brand_code?: string;
  branch_id: string;
  posted_at: string;
}

export interface TopicLeaderboardItem {
  topic: string;
  display_name: string;
  negative_count: number;
  total_reviews: number;
  negative_pct: number;
  prior_neg_count: number;
  trend: 'up' | 'down' | 'flat';
  top_branches: TopicAffectedBranch[];
  example_snippets: TopicExampleSnippet[];
}

// ── Discovery grid (bead lucky-war-fqm — /discovery page) ────────────────────

export type DiscoveryCardSeverity = 'alert' | 'watch' | 'insight';

export interface DiscoveryCard {
  type: string;
  severity: DiscoveryCardSeverity;
  title_th: string;
  so_what_th: string;
  owner_role: string;
  recency_badge: string;
  evidence: Record<string, unknown>;
  link: string;
}

export interface DiscoveryGridResponse {
  cards: DiscoveryCard[];
  is_experimental: boolean;
}

export function fetchDiscoveryGrid(signal?: AbortSignal, brand?: string): Promise<DiscoveryGridResponse> {
  return get<DiscoveryGridResponse>(`/discovery${buildQuery({ brand })}`, signal);
}

// ── Discovery customer voice (beads cw0b/jjpp/puu9 — /discovery/voice BFF) ──

export interface DiscoveryVoiceScope {
  brand: { code: string; name_th: string } | null; // chain lens = null
  days: 7 | 30;
  as_of: string;
  generated_at: string;
}

export interface DiscoveryVoiceWhere {
  scope: 'chain' | 'brand' | 'region' | 'branch';
  brand_code?: string;
  branch_id?: string | null;
  branch_name?: string | null;
  region_name?: string | null;
  branch_names?: string[];
}

export interface DiscoveryVoiceWhy {
  topic?: string;
  topic_label_th?: string;
  evidence_count: number;
  review_count?: number;
  enriched_count?: number;
  negative_count?: number;
  negative_pct?: number | null;
  baseline_review_count?: number;
  baseline_negative_count?: number;
  baseline_enriched_count?: number;
  baseline_negative_pct?: number | null;
  jump_pp?: number | null;
  geo_evidence_count?: number;
  recent_count?: number;
  baseline_count?: number;
  surge_ratio?: number | null;
  incident_type?: string;
}

export interface DiscoveryVoicePainRow {
  branch_id: string;
  branch_name: string;
  region_name: string;
  mention_count: number;
  negative_count: number;
  negative_pct: number;
}

export interface DiscoveryVoiceDrilldown {
  module: string;
  filter_key: string;
  filter_value: string;
  path: string;
}

export interface DiscoveryVoiceIncident {
  id: string;
  name_th: string;
  summary_th: string;
  incident_type: string;
  trigger: string;
  status: string;
  start_date: string;
  contained_date?: string;
  source_urls: string[];
  evidence_count: number;
}

export interface DiscoveryVoiceSignal {
  id: string;
  type: string;
  severity: string;
  priority: number;
  title_th: string;
  what_changed_th: string;
  where: DiscoveryVoiceWhere;
  why: DiscoveryVoiceWhy;
  native_window: string;
  baseline: string;
  drilldown: DiscoveryVoiceDrilldown;
  pain_matrix?: DiscoveryVoicePainRow[];
  orthogonal?: boolean;
  incident?: DiscoveryVoiceIncident | null;
}

export interface DiscoveryVoiceEvidenceItem {
  id: string;
  external_id: string;
  source: string;
  source_label: string;
  brand_code?: string;
  branch_id: string | null;
  branch_name: string | null;
  region_name: string | null;
  unmatched: boolean;
  attribution_scope?: string | null;
  attribution_reason?: string | null;
  attribution_source?: string | null;
  branch_confidence?: number | null;
  text: string;
  rating: number | null;
  sentiment: string | null;
  topics: string[];
  posted_at: string;
  permalink: string | null;
  source_url: string | null;
}

export interface DiscoveryVoiceReviewDenominators {
  google_maps?: number;
  wongnai?: number;
  total?: number;
  [source: string]: number | undefined;
}

export interface DiscoveryVoiceDenominators {
  reviews_in_window: DiscoveryVoiceReviewDenominators;
  fb_comments_in_window: number;
}

export interface DiscoveryVoiceEvidence {
  items: DiscoveryVoiceEvidenceItem[];
  next_cursor: string | null;
  denominators: DiscoveryVoiceDenominators;
}

export interface DiscoveryVoiceSourceFreshness {
  last_run_at: string | null;
  status: 'fresh' | 'stale' | 'unavailable';
  degraded: boolean;
  message_th: string;
}

export interface DiscoveryVoiceFreshness {
  google_maps: DiscoveryVoiceSourceFreshness;
  wongnai: DiscoveryVoiceSourceFreshness;
  facebook: DiscoveryVoiceSourceFreshness;
}

export interface DiscoveryVoiceResponse {
  scope: DiscoveryVoiceScope;
  signals_to_check: DiscoveryVoiceSignal[];
  customer_voice_evidence: DiscoveryVoiceEvidence;
  freshness: DiscoveryVoiceFreshness;
  filters?: {
    topic: string;
    branch: string;
    incident: boolean;
    incident_id?: string;
  };
}

export function fetchDiscoveryVoice(
  opts: {
    brand?: string;
    days?: 7 | 30;
    cursor?: string;
    perPage?: number;
    topic?: string;
    branch?: string;
    incident?: boolean;
    incidentId?: string;
  },
  signal?: AbortSignal,
): Promise<DiscoveryVoiceResponse> {
  return get<DiscoveryVoiceResponse>(
    `/discovery/voice${buildQuery({
      brand: opts.brand,
      days: opts.days,
      cursor: opts.cursor,
      perPage: opts.perPage,
      topic: opts.topic,
      branch: opts.branch,
      incident: opts.incident ? 1 : undefined,
      incident_id: opts.incidentId,
    })}`,
    signal,
  );
}

export interface PromoCannibalizationTopItem {
  item_code: string; item_name_th: string; attach_baseline: number; attach_campaign: number;
  delta_pp: number; cannibalized: boolean;
}
export interface PromoCannibalizationItem {
  campaign_id: string; campaign_name_th: string; window_start: string; window_end: string;
  baseline_start: string; baseline_end: string; status: 'measured' | 'dormant';
  days_measured: number; days_baseline: number; flagged_count: number;
  top_items: PromoCannibalizationTopItem[];
}
export interface PromoCannibalizationResponse { items: PromoCannibalizationItem[]; threshold_pp: number; }
export function fetchPromoCannibalization(signal?: AbortSignal, brand?: string): Promise<PromoCannibalizationResponse> {
  return get<PromoCannibalizationResponse>(`/discovery/promo-cannibalization${buildQuery({ brand })}`, signal);
}

// ── Campaign impact (A7) ─────────────────────────────────────────────────────

export type CampaignPhase = 'upcoming' | 'live' | 'post';

/** Confirmed-channel evidence joined to a campaign's demand result (kvd7; read-side only). */
export interface CampaignChannelAssociation {
  channel: string; // facebook (only mappable source today)
  linked_post_count: number;
  linked_post_permalinks: string[];
  demand_basis: string; // campaign_metrics
  shared_across_channels: boolean;
}

export interface CampaignImpactItem {
  id: string;
  name_th: string;
  phase: CampaignPhase;
  start_date: string;
  end_date: string;
  day_of_window: number;
  total_days: number;
  has_baseline: boolean;
  directional: boolean;
  attach_rate: number | null;
  metrics_computed_at?: string;
  campaign_metrics?: Record<string, unknown>;
  channel_associations?: CampaignChannelAssociation[];
}

export interface CampaignImpactResponse {
  items: CampaignImpactItem[];
  is_experimental: boolean;
}

export function fetchCampaignImpact(signal?: AbortSignal, brand?: string): Promise<CampaignImpactResponse> {
  return get<CampaignImpactResponse>(`/discovery/campaign-impact${buildQuery({ brand })}`, signal);
}

// ── Unattributed VC codes (A8) ───────────────────────────────────────────────

export interface UnattributedVCCode {
  code: string;
  period: string;
  total_baht: number;
  campaign_ref: string;
}

export interface UnattributedVCResponse {
  items: UnattributedVCCode[];
  window_days: number;
  is_experimental: boolean;
}

export function fetchUnattributedVC(signal?: AbortSignal, brand?: string): Promise<UnattributedVCResponse> {
  return get<UnattributedVCResponse>(`/discovery/unattributed-vc${buildQuery({ brand })}`, signal);
}

// ── Item winners/losers (bead lucky-war-urbs — k357 gate pass 2026-09-01) ────

export interface ItemMover {
  item_code: string;
  item_name_th: string;
  cur_qty: number;
  prev_qty: number;
  delta_qty: number;
  /** null when prev_qty == 0 (new item — no comparable baseline). */
  pct_change: number | null;
  cur_net: number;
  prev_net: number;
}

export interface ItemMoversSummary {
  status: 'ok' | 'collecting';
  cur_start: string;
  cur_end: string;
  prev_start: string;
  prev_end: string;
  cur_days: number;
  prev_days: number;
  winners: ItemMover[];
  losers: ItemMover[];
  new_items: ItemMover[];
}

export interface ItemMoversThresholds {
  min_prev_qty: number;
  min_pct_change: number;
  min_new_qty: number;
  min_days: number;
  top_n: number;
}

export interface ItemMoversResponse {
  summary: ItemMoversSummary;
  thresholds: ItemMoversThresholds;
}

export function fetchDiscoveryItemMovers(signal?: AbortSignal, brand?: string): Promise<ItemMoversResponse> {
  return get<ItemMoversResponse>(`/discovery/item-movers${buildQuery({ brand })}`, signal);
}

// ── Discount-dependency quadrant (bead lucky-war-rb2p — k357 gate pass) ──────

export type DiscountQuadrantKey = 'healthy_growth' | 'discount_driven' | 'discount_trap' | 'no_growth_low_discount';

export interface DiscountQuadrantPoint {
  branch_id: string;
  branch_name: string;
  growth_pct: number; // WoW net_total %, 1dp
  depth: number; // cur-window discount/gross, 4dp
  cur_net: number;
  prev_net: number;
  cur_days: number;
  prev_days: number;
  quadrant: DiscountQuadrantKey;
}

export interface DiscountQuadrantResponse {
  status: 'ok' | 'collecting';
  reason?: string;
  cur_start: string;
  cur_end: string;
  prev_start: string;
  prev_end: string;
  median_depth: number;
  threshold_depth: number; // effective "deep" split = max(median_depth, 1% floor)
  threshold_basis: 'median' | 'floor';
  quadrant_counts?: Record<DiscountQuadrantKey, number>;
  points: DiscountQuadrantPoint[];
  excluded_count: number;
  is_experimental: boolean;
  honesty_footer: string;
}

export function fetchDiscountQuadrant(signal?: AbortSignal, brand?: string): Promise<DiscountQuadrantResponse> {
  return get<DiscountQuadrantResponse>(`/discovery/discount-quadrant${buildQuery({ brand })}`, signal);
}

// ── Sales snapshot (per-branch) ──────────────────────────────────────────────

export interface PromoChannel {
  name: string;
  baht: number;
}

export interface SalesSnapshotResponse {
  net_total: number;
  gross_sales: number;
  discount: number;
  discount_pct: number;
  bill_count: number;
  cust_count: number;
  avg_spend_per_head: number;
  avg_discount_per_bill: number;
  avg_net_per_bill?: number;
  top_promo_channels: PromoChannel[];
  sales_date: string;
  is_experimental: boolean;
  is_empty: boolean;
  night_cust_count: number | null;
  night_pct_of_day: number | null;
  mtd_net_total?: number | null;
  wow_delta_pct?: number | null;
  wow_delta_available?: boolean;
  cust_split?: CustSplit;
  /** Effective ICT window; null for the legacy latest-period response. */
  window: MetricWindow | null;
}

export function fetchSalesSnapshot(
  branchId: string,
  signal?: AbortSignal,
  window?: MetricWindowParams,
): Promise<SalesSnapshotResponse> {
  return get<SalesSnapshotResponse>(
    `/branches/${encodeURIComponent(branchId)}/sales-snapshot${buildQuery({ ...(window ?? {}) } as Record<string, string | number | undefined>)}`,
    signal,
  );
}

// ── Sales overview (chain-wide) ───────────────────────────────────────────────

export interface SalesBranchRef {
  branch_id: string;
  name_th: string;
  shop_code: string;
  net_total: number;
}

// SalesFreshness — honesty stamp for sales-derived cards (mgmt feedback 2026-08-13).
// last_ingest_at = when the last sales file was loaded; data_through = the latest
// calendar day the data actually reaches (usually yesterday). Rendered as
// "อัปเดตล่าสุด … · ข้อมูลถึง …".
export interface SalesFreshness {
  last_ingest_at: string;
  data_through: string;
}

export interface SalesOverviewResponse {
  net_total: number;
  discount_total: number;
  discount_pct: number;
  bill_count: number;
  cust_count: number;
  // Branches with ≥1 sales row in the period (per-branch denominator, bead gp83)
  branches_with_sales?: number;
  top_branches: SalesBranchRef[];
  bottom_branches: SalesBranchRef[];
  promo_channels: PromoChannel[];
  delivery_app_pct: number;
  sales_date: string;
  last_updated_at: string | null;
  is_experimental: boolean;
  is_empty: boolean;
  // Chain-wide night traffic (bead lucky-war-z3m feat 1)
  night_cust_count: number;
  night_pct_of_day: number;
  // MTD mode (mgmt feedback 2026-08-13)
  is_mtd?: boolean;
  freshness?: SalesFreshness;
  sales_mix?: SalesMix;
}

export function fetchSalesOverview(
  opts?: { week?: string; range?: 'mtd'; brand?: string },
  signal?: AbortSignal,
): Promise<SalesOverviewResponse> {
  const params = new URLSearchParams();
  if (opts?.week) params.set('week', opts.week);
  if (opts?.range) params.set('range', opts.range);
  if (opts?.brand) params.set('brand', opts.brand);
  const qs = params.toString();
  return get<SalesOverviewResponse>(`/sales/overview${qs ? `?${qs}` : ''}`, signal);
}

// ── Sales trend (bead lucky-war-4qr.6) ────────────────────────────────────────

export interface SalesTrendDay {
  date: string;
  net_total: number;
  bill_count: number;
  cust_count: number;
  sales_mix?: SalesMix;
}

export interface SalesTrendResponse {
  days: SalesTrendDay[];
  is_experimental: boolean;
  is_empty: boolean;
}

export function fetchSalesTrend(days?: number, signal?: AbortSignal, brand?: string, branchId?: string): Promise<SalesTrendResponse> {
  const qs = buildQuery({ days: days != null ? Math.max(1, Math.min(90, days)) : undefined, brand, branch_id: branchId });
  return get<SalesTrendResponse>(`/sales/trend${qs}`, signal);
}

export interface DeliveryTrendDay {
  date: string;
  delivery_total: number;
  net_total: number;
  delivery_app_pct: number;
  delivery_apps?: Record<string, number>;
}
export interface DeliveryTrendFlag {
  branch_id: string;
  branch_name_th: string;
  current_pct: number;
  prev_pct: number;
  shift_pp: number;
  months_compared: string[];
}
export interface SalesDataCoverage {
  days_with_data: number;
  first_date: string;
  last_date: string;
  months_available: string[];
}
export interface DeliveryTrendResponse {
  series: DeliveryTrendDay[];
  flags: DeliveryTrendFlag[];
  data_coverage: SalesDataCoverage;
}
export function fetchDeliveryTrend(opts?: { branchId?: string; brand?: string; days?: number }, signal?: AbortSignal): Promise<DeliveryTrendResponse> {
  return get<DeliveryTrendResponse>(`/sales/delivery-trend${buildQuery({ branch_id: opts?.branchId, brand: opts?.brand, days: opts?.days })}`, signal);
}

// ── Delivery share summary (bead lucky-war-8921) ─────────────────────────────

export interface DeliveryAppShare {
  code: string;
  share: number;
}
export interface DeliverySummaryResponse {
  window_start: string;
  window_end: string;
  window_days: number;
  days_with_data: number;
  data_through?: string;
  delivery_pct: number | null;
  prev_delivery_pct: number | null;
  comparison_available: boolean;
  delta_pp: number | null;
  delivery_apps: DeliveryAppShare[];
}
export function fetchDeliverySummary(opts?: { branchId?: string; brand?: string }, signal?: AbortSignal): Promise<DeliverySummaryResponse> {
  return get<DeliverySummaryResponse>(`/sales/delivery-summary${buildQuery({ branch_id: opts?.branchId, brand: opts?.brand })}`, signal);
}

// ── Item sales (bead lucky-war-0ke) ───────────────────────────────────────────

export interface ItemSalesEntry {
  item_code: string;
  name_th: string;
  unit_price: number;
  child_price?: number;
  revenue_source: 'unit_price' | 'base_sales';
  total_qty: number;
  adult_units: number;
  kid_units: number;
  computed_sales: number;
  revenue_rank: number;
  anomaly_flag: boolean;
  z_score?: number;
}

export interface ItemSalesSnapshotResponse {
  branch_id: string;
  items: ItemSalesEntry[];
  total_item_sales: number;
  sales_date: string;
  is_experimental: boolean;
  is_empty: boolean;
}

export interface ItemSalesOverviewResponse {
  items: ItemSalesEntry[];
  total_item_sales: number;
  sales_date: string;
  is_experimental: boolean;
  is_empty: boolean;
  // MTD mode (mgmt feedback 2026-08-13)
  is_mtd?: boolean;
  freshness?: SalesFreshness;
}

export function fetchItemSalesSnapshot(branchId: string, signal?: AbortSignal): Promise<ItemSalesSnapshotResponse> {
  return get<ItemSalesSnapshotResponse>(`/branches/${encodeURIComponent(branchId)}/sales-items`, signal);
}

export function fetchItemSalesOverview(
  range?: 'mtd',
  signal?: AbortSignal,
  brand?: string,
): Promise<ItemSalesOverviewResponse> {
  const qs = buildQuery({ range, brand });
  return get<ItemSalesOverviewResponse>(`/sales/items/overview${qs}`, signal);
}

// ── Admin alerts (jjn.5) ─────────────────────────────────────────────────────

export interface VoucherOccurrence {
  name: string;
  amount_baht: number;
  branch_id: string;
  shop_code: string;
  branch_name: string;
  period: string;
  source_ref: string;
  filename: string;
  ingested_at: string;
  report_subtype: string;
  time_meta: string;
}

export interface VoucherAlertEvidence {
  code: string;
  name: string;
  /** Sum of daily snapshots only; overlapping subreports stay evidence-only. */
  total_baht: number | null;
  total_overflow: boolean;
  occurrences: VoucherOccurrence[];
}

export interface AlertEvidence {
  vouchers?: VoucherAlertEvidence[];
  window_days?: number;
  [key: string]: unknown;
}

export interface AdminAlert {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  source: 'scrape' | 'disk' | 'enrichment_drift' | 'vc_codes' | 'rollup' | 'competitive';
  title: string;
  detail: string;
  first_observed: string;
  last_observed: string;
  evidence: AlertEvidence;
}

export interface AdminAlertsResponse {
  alerts: AdminAlert[];
  checked_at: string;
  total_count: number;
}

// ── Weekly digest (lucky-war-di0) ────────────────────────────────────────────

export interface WeeklyKpiDelta {
  current: number;
  previous: number;
  delta: number;
}

export interface WeeklyRating {
  current: number;
  previous: number;
  delta: number;
  source_label: string;
}

export interface WeeklyReviewVolume {
  current: number;
  previous: number;
  delta: number;
}

export interface WeeklyNegativePct {
  current: number;
  previous: number;
  delta: number;
}

export interface WeeklyTopic {
  topic: string;
  count: number;
  label_th: string;
  evidence?: WeeklyTopicEvidence[];
}

export interface WeeklyTopicEvidence {
  source: string;
  text: string;
  posted_at: string;
  permalink: string | null;
  branch_id: string | null;
  branch_name: string | null;
  attribution_scope: string | null;
}

export interface WeeklySalesDay {
  date: string;          // YYYY-MM-DD (ICT)
  net_total: number;
  bill_count: number;
  cust_count: number;
  has_data: boolean;     // false → ghost bar, never ฿0
}

export interface WeeklySalesDelta {
  current: number;
  previous: number;      // matched-weekday baseline (see comparison_available)
  delta: number;
  delta_pct: number;
  bill_count: number;
  cust_count: number;
  prev_bill_count: number;
  prev_cust_count: number;
  bill_delta_pct: number | null;
  cust_delta_pct: number | null;
  branches_up: number;
  branches_down: number;
  is_available: boolean;
  comparison_available: boolean;  // ≥1 weekday has sales in BOTH weeks (same-weekday gate)
  matched_days: number;           // how many weekdays the WoW comparison covers (0–7)
  branches_reporting: number;     // branches with ≥1 sales row this week
  branches_total: number;         // total active branches
  daily: WeeklySalesDay[];        // week-bounded Mon–Sun series (always 7 entries)
  cust_split?: CustSplit;
}

export interface WeeklyPromoLift {
  voucher_total: number;
  gross_total: number;
  lift_pct: number;
  is_available: boolean;
}

export interface WeeklyChain {
  rating: WeeklyRating | null;
  review_volume: WeeklyReviewVolume | null;
  negative_pct: WeeklyNegativePct | null;
  sales_delta: WeeklySalesDelta | null;
  promo_lift: WeeklyPromoLift | null;
  top_topics: WeeklyTopic[];
}

export interface WeeklyMover {
  branch_id: string;
  name_th: string;
  region: string;
  metric: string;
  delta: number;
}

export interface WeeklyTopMovers {
  improving: WeeklyMover[];
  declining: WeeklyMover[];
}

export interface WeeklyRegion {
  region_id: string;
  name_th: string;
  branch_count: number;
  rating: WeeklyKpiDelta | null;
  review_volume: WeeklyReviewVolume | null;
  negative_pct: WeeklyNegativePct | null;
  sales: WeeklyRegionSales | null;
}

export interface WeeklyRegionSales {
  current: number;
  previous: number;
  delta_pct: number;
  per_branch: number;
  is_available: boolean;
  comparison_available: boolean;
}

export interface WeeklyFbLabels {
  reach_impressions: string;
  lag: string;
}

export interface WeeklyFbSummary {
  comment_count: number;
  analyzed_count: number;
  negative_count: number;
  negative_pct: number | null;
  unmatched_count: number;
  unmatched_pct: number | null;
  top_topics: WeeklyTopic[];
  evidence_count: number;
  labels: WeeklyFbLabels;
}

export interface WeeklyFeedbackSummary {
  review_count: number;
  fb_comment_count: number;
  total_count: number;
  analyzed_count: number;
  negative_count: number;
  negative_pct: number | null;
}

export interface WeeklyFreshnessSource {
  last_run_at: string | null;
  status: string;
}

export interface WeeklyFreshness {
  google_maps: WeeklyFreshnessSource;
  facebook: WeeklyFreshnessSource;
  sales: WeeklyFreshnessSource;
}

export interface WeeklyHonestyNotes {
  rating_source: string;
  fb_unmatched_disclaimer: string;
  feed_enrichment_lag: string;
  feed_cap: string;
  feed_fb_brand_note: string;
}

// ── Weekly feedback feed (bead lucky-war-daoh.1) ──────────────────────────

export interface WeeklyFeedbackEntry {
  source: string;
  external_id: string;
  text: string;
  posted_at: string;
  permalink: string | null;
  topics: string[];
  sentiment: string | null;
  sentiment_confidence: number | null;
  branch_id: string | null;
  branch_name: string | null;
  rating: number | null;
  author_name: string | null;
  attribution_scope: string | null;
  reaction_count: number | null;
}

export interface WeeklyResponse {
  week_id: string;
  week_start: string;
  week_end: string;
  generated_at: string;
  chain: WeeklyChain;
  top_movers: WeeklyTopMovers;
  regions: WeeklyRegion[];
  fb_summary: WeeklyFbSummary | null;
  feedback_summary: WeeklyFeedbackSummary | null;
  freshness: WeeklyFreshness;
  honesty_notes: WeeklyHonestyNotes;
  feedback_feed: WeeklyFeedbackEntry[];
}

export function fetchWeeklyDigest(week?: string, signal?: AbortSignal, brand?: string): Promise<WeeklyResponse> {
  const qs = buildQuery({ week, brand });
  return get<WeeklyResponse>(`/weekly${qs}`, signal);
}

// ── Promotions (bead lucky-war-z3m feat 2) ────────────────────────────────────

export interface PromotionSales {
  units_sold: number;
  revenue: number;
}

export interface Promotion {
  id: string;
  name_th: string;
  description_th: string;
  item_codes: string[];
  start_date: string;
  end_date: string;
  price_adult: number;
  price_child: number;
  brand_scope: string[];
  branch_scope?: string[];
  branch_scope_names?: string[];
  status: string;
  is_active: boolean;
  sales: PromotionSales | null;
}

export interface PromotionsResponse {
  promotions: Promotion[];
  is_experimental: boolean;
}

export function fetchPromotions(signal?: AbortSignal, brand?: string): Promise<PromotionsResponse> {
  return get<PromotionsResponse>(`/promotions${buildQuery({ brand })}`, signal);
}

// ── Sales upload panel (bead lucky-war-8n7w) ──────────────────────────────────

export interface SalesUploadFileResult {
  filename: string;
  sha256: string;
  status: 'queued' | 'deduped' | 'rejected';
  reason: string;
  ingest_run_id: string | null;
  raw_files_id: string | null;
}

export interface SalesUploadResponse {
  results: SalesUploadFileResult[];
  ingest_run_ids: string[];
}

/**
 * Upload one or more .xlsx sales files. Files are sent as multipart/form-data
 * under the "files" field. Requires superuser auth (403 otherwise).
 */
export function uploadSalesFiles(files: File[], signal?: AbortSignal): Promise<SalesUploadResponse> {
  const fd = new FormData();
  for (const f of files) {
    fd.append('files', f);
  }
  return postMultipart<SalesUploadResponse>('/sales/upload', fd, signal);
}

export interface SalesUploadItem {
  ingest_run_id: string;
  filename: string;
  sha256: string;
  status: string; // queued | running | succeeded | failed | partial
  started_at: string;
  finished_at: string | null;
  duration_seconds: number | null;
  rows_written: number;
  unmapped_count: number;
  error_summary: string | null;
}

export interface SalesUploadsResponse {
  items: SalesUploadItem[];
  limit: number;
}

/**
 * Fetch recent sales upload history. Requires superuser auth (403 otherwise).
 */
export function fetchSalesUploads(limit?: number, signal?: AbortSignal): Promise<SalesUploadsResponse> {
  const qs = limit != null ? `?limit=${Math.max(1, Math.min(100, limit))}` : '';
  return get<SalesUploadsResponse>(`/sales/uploads${qs}`, signal);
}

// ── Fusion scatter (bead lucky-war-t87) ─────────────────────────────────────

export interface FusionScatterPoint {
  branch_id: string;
  branch_name: string;
  promo_pct: number;
  negative_pct: number;
  review_count: number;
  sales_periods: number;
}

export interface FusionScatterResponse {
  gated: boolean;
  reason?: string;
  points: FusionScatterPoint[];
  median_promo_pct: number;
  median_negative_pct: number;
  sales_period_start: string;
  sales_period_end: string;
  period_count: number;
  is_experimental: boolean;
  honesty_footer: string;
}

export function fetchFusionScatter(signal?: AbortSignal, brand?: string): Promise<FusionScatterResponse> {
  return get<FusionScatterResponse>(`/discovery/fusion-scatter${buildQuery({ brand })}`, signal);
}

// ── Rating drop → sales impact (bead lucky-war-oktt) ────────────────────────
export interface FusionImpactSales {
  pre_net_total: number;
  post_net_total: number;
  delta_pct: number | null;
  pre_days_covered: number;
  post_days_covered: number;
  state: 'ready' | 'partial' | 'awaiting_sales' | 'no_baseline';
}
export interface FusionImpactEvent {
  branch_id: string;
  branch_name: string;
  week: string;
  rating_this: number;
  rating_prev: number;
  rating_delta: number;
  review_count_week: number;
  review_count_prev: number;
  sales: FusionImpactSales;
}
export interface FusionImpactResponse {
  gated: boolean;
  reason: string;
  comparison_available: boolean;
  window_weeks: number;
  rating_source_th: string;
  events: FusionImpactEvent[];
  methodology_note: string;
  honesty_footer: string;
}
export function fetchFusionImpact(brand?: string, signal?: AbortSignal): Promise<FusionImpactResponse> {
  return get<FusionImpactResponse>(`/fusion/impact${buildQuery({ brand })}`, signal);
}

// ── Sales monthly progress (A4 Stream A — lucky-war-8tm5/t8n0) ────────────────

export interface SalesMonthlyProgressResponse {
  target: number | null;
  target_status: 'ok' | 'not_configured';
  achieved_mtd: number;
  remaining: number;
  days_elapsed: number;
  days_remaining: number;
  daily_burn_needed: number;
  projected_eom: number;
  is_on_track: boolean;
  as_of_date: string;
  sales_mix?: SalesMix;
}

export function fetchSalesMonthlyProgress(signal?: AbortSignal, brand?: string): Promise<SalesMonthlyProgressResponse> {
  return get<SalesMonthlyProgressResponse>(`/sales/monthly-progress${buildQuery({ brand })}`, signal);
}

// ── Extended branch list (A4 Stream A — brand + per-head + promo-cost fields) ──

/** One day in the per-head trend series. */
export interface PerHeadDay {
  date: string;
  per_head: number | null;
  chain_avg: number;
}

/** Promo cost ratio object shape (server returns {value, chain_avg}). */
export interface PromoCostRatio {
  value: number;
  chain_avg: number;
}

export interface RankingBranchSummary extends BranchSummary {
  brand_id: string;
  brand_name_th: string;
  /** Per-head spend trend: one {date, per_head, chain_avg} per day. Null entries = no data for that day. */
  per_head_trend_14d: PerHeadDay[];
  /** Promo cost ratio (discount/gross) over last 7 days. */
  promo_cost_ratio_7d: PromoCostRatio;
  /** Month-to-date net total. */
  mtd_net_total: number | null;
  /** Sales totals for the requested metric window; absent on legacy payloads. */
  window_sales?: WindowSales | null;
  /**
   * Chain-wide dense rank by MTD net_total over ALL active branches
   * (server-computed, filter-independent). Null when the branch has no
   * MTD sales. Shown as the fixed #badge so it never renumbers under
   * search/region/brand/topic filtering.
   */
  mtd_rank?: number | null;
  /** Day-over-day delta percentage (today vs yesterday). Null when only 1 day of data. */
  dod_delta_pct: number | null;
}

export function fetchBranchRanking(
  params?: { region?: string; brand?: string; q?: string; topic?: string } & MetricWindowParams,
  signal?: AbortSignal,
): Promise<RankingBranchSummary[]> {
  const searchParams = new URLSearchParams();
  // '_all' is the region-compare sentinel for "no region"; /branches expects it omitted.
  if (params?.region && params.region !== '_all') searchParams.set('region', params.region);
  if (params?.brand) searchParams.set('brand', params.brand);
  if (params?.q) searchParams.set('q', params.q);
  if (params?.topic) searchParams.set('topic', params.topic);
  if (params?.days) searchParams.set('days', String(params.days));
  if (params?.from) searchParams.set('from', params.from);
  if (params?.to) searchParams.set('to', params.to);
  searchParams.set('perPage', '500');
  const qs = searchParams.toString();
  return get<{ items: RankingBranchSummary[] }>(`/branches${qs ? '?' + qs : ''}`, signal).then((r) => r.items);
}

// ── Region scorecard (/branch?region= deep link, bead lucky-war-gv27) ───────

/** Matched-day WoW sales block (same shape as the overview region cards). */
export interface RegionSalesWow {
  current: number;
  delta_pct: number;
  comparison_available: boolean;
}

/** Night-traffic share over the trailing 7 ICT days; null when no night rows. */
export interface RegionNightShare {
  pct: number;
  night_cust: number;
  total_cust: number;
}

/** One worst-first watchlist row (30-day negative %, per-branch sample gate). */
export interface RegionWatchItem {
  branch_id: string;
  name_th: string;
  brand_code: string;
  negative_pct: number | null;
  enriched_count: number;
  rating: number | null;
  review_count: number | null;
}

/**
 * Region-level aggregates, filter-then-aggregate: weighted Google rating,
 * review-weighted negative %, matched-day sales WoW with branch coverage,
 * sales-weighted promo ratio, 7-day night share, and the watchlist.
 * Nulls are honest — no data, never zero.
 */
export interface RegionSummaryResponse {
  region_id: string;
  region_name: string;
  branch_count: number;
  single_branch: boolean;
  rating_avg: number | null;
  rating_source_label: string;
  rating_weight_sum: number;
  review_count_total: number;
  review_volume_30d: number;
  negative_pct: number | null;
  negative_count_30d: number;
  enriched_count_30d: number;
  sales_wow: RegionSalesWow | null;
  branches_reporting: number;
  branches_comparable: number;
  promo_cost_ratio_7d: PromoCostRatio | null;
  night_share_7d: RegionNightShare | null;
  watchlist: RegionWatchItem[];
  watchlist_suppressed: boolean;
  insight_flags: string[];
  window?: MetricWindow;
  window_effective?: boolean;
}

export function fetchRegionSummary(
  regionId: string,
  brand?: string,
  signal?: AbortSignal,
  window?: MetricWindowParams,
): Promise<RegionSummaryResponse> {
  return get<RegionSummaryResponse>(
    `/regions/${encodeURIComponent(regionId)}/summary${buildQuery({ brand, ...window })}`,
    signal,
  );
}

// ── Promo attach carousel (B3 Stream B — lucky-war-mzcp) ─────────────────────

export interface PromoAttachBranch {
  branch_id: string;
  name_th: string;
  units_today: number;
  attach_rate: number;
  z_score: number | null;
  anomaly_flag: boolean;
}

export interface PromoAttachSplit {
  adult_units: number;
  kid_units: number;
}

export interface PromoAttachItem {
  item_code: string;
  name_th: string;
  brand_scope: string;
  chain: {
    units_today: number;
    attach_rate_today: number;
    attach_14d_baseline: number;
    z_score: number | null;
    anomaly_flag: boolean;
    daily_attach_series?: (number | null)[];
  };
  adult_kid_split: PromoAttachSplit;
  branches: PromoAttachBranch[];
}

export interface PromoAttachResponse {
  as_of_date: string;
  items: PromoAttachItem[];
  methodology_note: string;
  // MTD mode (mgmt feedback 2026-08-13)
  is_mtd?: boolean;
  freshness?: SalesFreshness;
}

export function fetchPromoAttach(
  range?: 'mtd',
  signal?: AbortSignal,
  brand?: string,
): Promise<PromoAttachResponse> {
  const qs = buildQuery({ range, brand });
  return get<PromoAttachResponse>(`/promos/attach${qs}`, signal);
}

// ── Social overview (all four FB/IG entities; verified metric set) ──────────

export type SocialProvenance = 'fresh' | 'stale' | 'unavailable';

/** One window (current or prior) of live Graph metrics for one entity. */
export interface SocialMetrics {
  reach: number | null;       // FB page reach / IG reach (sum of daily rows)
  views: number | null;       // page_media_view (sum)
  engagement: number | null;  // page_post_engagements (sum)
  new_follows: number | null; // FB page_daily_follows / IG follower_count daily net-new (sum)
  page_views: number | null;  // page_views_total (sum)
  followers: number | null;   // FB page_follows / IG profile followers_count (cumulative)
  posts_count?: number | null; // locally synced fb_posts in window (cadence context)
}

export interface SocialEntity {
  id: string;              // suki_fb | suki_ig | bbq_fb | bbq_ig
  chain: string;           // suki | bbq
  platform: string;        // facebook | instagram
  provenance: SocialProvenance;
  reason?: string;         // Thai hint when degraded
  updated_at?: string;
  metrics?: SocialMetrics;
  prior?: SocialMetrics;
}

export interface SocialOverviewResponse {
  generated_at: string;
  period: number; // 7 | 28
  entities: SocialEntity[];
}

export function fetchSocialOverview(
  period: 7 | 28,
  signal?: AbortSignal,
  brand?: string,
): Promise<SocialOverviewResponse> {
  return get<SocialOverviewResponse>(`/social/overview${buildQuery({ period, brand })}`, signal);
}

// ── Instagram media (bounded live source-native content feed) ───────────────

export interface SocialInstagramMediaItem {
  id: string;
  caption_excerpt: string;
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM' | string;
  media_product_type: 'FEED' | 'REELS' | string;
  permalink: string;
  thumbnail_url?: string;
  timestamp: string;
  likes: number | null;
  comments: number | null;
  interactions: number | null; // likes + comments only; no invented IG ER
}

export interface SocialInstagramMediaSummary {
  media_count: number;
  likes: number | null;
  comments: number | null;
  interactions: number | null;
  truncated: boolean;
}

export interface SocialInstagramMediaEntity {
  id: string;
  chain: 'suki' | 'bbq';
  brand_code: 'lucky_suki' | 'lucky_bbq';
  account_handle: string;
  provenance: SocialProvenance;
  reason?: string;
  updated_at?: string;
  summary?: SocialInstagramMediaSummary;
  items: SocialInstagramMediaItem[];
}

export interface SocialInstagramMediaResponse {
  generated_at: string;
  period_days: 7 | 28;
  entities: SocialInstagramMediaEntity[];
}

export function fetchSocialInstagramMedia(
  period: 7 | 28 = 28,
  signal?: AbortSignal,
  brand?: string,
): Promise<SocialInstagramMediaResponse> {
  return get<SocialInstagramMediaResponse>(`/social/instagram-media${buildQuery({ period, brand })}`, signal);
}

// ── Social TikTok import (bead lucky-war-n182 — source-specific snapshot) ────

/** TikTok is a manual file import, not a live Meta entity. */
export type SocialTikTokProvenance = 'manual' | 'unavailable';

export interface SocialTikTokCoverage {
  complete_days: number | null;
  expected_days: number | null;
}

/** Account-level totals from the imported TikTok export. */
export interface SocialTikTokSummary {
  views: number | null;
  profile_views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  interaction_rate_pct: number | null;
  followers: number | null;
  follower_delta: number | null;
  viewers: number | null;
  new_viewers: number | null;
  returning_viewers: number | null;
  new_viewer_pct: number | null;
  returning_viewer_pct: number | null;
}

export interface SocialTikTokBreakdown {
  label: string;
  value: number | null;
  unit: 'percent' | 'count';
}

export interface SocialTikTokAudience {
  gender: SocialTikTokBreakdown[] | null;
  territories: SocialTikTokBreakdown[] | null;
  peak_hours: SocialTikTokBreakdown[] | null;
}

export interface SocialTikTokContentItem {
  id: string | null;
  title: string | null;
  permalink: string | null;
  post_date: string | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  interaction_rate_pct: number | null;
}

export type SocialTikTokContentPayload = SocialTikTokContentItem[] | null;
export type SocialTikTokWarning = string;

export interface SocialTikTokResponse {
  generated_at: string;
  period_days: 7 | 28;
  provenance: SocialTikTokProvenance;
  reason?: string | null;
  account_handle: string | null;
  brand_code: string;
  imported_at: string | null;
  source_as_of_date: string | null;
  coverage: SocialTikTokCoverage | null;
  summary: SocialTikTokSummary | null;
  audience: SocialTikTokAudience | null;
  content: SocialTikTokContentPayload;
  warnings: SocialTikTokWarning[] | null;
}

export function fetchSocialTikTok(
  days: 7 | 28 = 28,
  signal?: AbortSignal,
  brand?: string,
): Promise<SocialTikTokResponse> {
  return get<SocialTikTokResponse>(`/social/tiktok${buildQuery({ days, brand })}`, signal);
}

// ── Social top posts (bead lucky-war-hmbr — "โพสต์เด่นช่วงนี้" module) ────────

/** Per-post comment aggregates from fb_comments enrichment. */
export interface SocialPostComments {
  ingested: number;
  enriched: number;
  positive: number;
  neutral: number;
  negative: number;
  visit_intent: number;
  complaint: number;
}

export interface SocialPostPromo {
  status: 'draft' | 'active' | 'ended';
}

/** One ranked post card. bucket: rate | volume | new (รอข้อมูลนิ่ง). */
export interface SocialPostCard {
  id: string;
  external_id: string;
  created_at: string;
  message_excerpt: string;
  media_type: 'photo' | 'video' | 'other' | '';
  permalink_url: string;
  reactions: number;
  comments: number;
  shares: number;
  engagement: number;
  reach?: number;
  engagement_rate_pct?: number; // raw rate; ranking shrinkage is internal
  bucket: 'rate' | 'volume' | 'new';
  rank_reason?: string;
  reach_is_estimated: boolean;
  promo?: SocialPostPromo;
  comments_stats?: SocialPostComments;
}

export interface SocialPostsSummary {
  posts_in_period: number;
  mature_posts: number;
  posts_with_reach: number;
  reach_coverage_pct: number;
  new_posts: number;
  rate_eligible_posts: number;
  truncated: boolean;
}

/** Median comparison row; deltas are null when sample-size-suppressed. */
export interface SocialPostsCompare {
  median_rate_pct_current?: number;
  median_rate_pct_prior?: number;
  median_rate_delta_pp?: number;
  avg_engagement_current?: number;
  avg_engagement_prior?: number;
  avg_engagement_delta_pct?: number;
  n_current: number;
  n_prior: number;
  n_rate_current: number;
  n_rate_prior: number;
  status: 'ok' | 'partial' | 'insufficient';
}

export interface SocialPostsBrand {
  code: string; // lucky_suki | lucky_bbq
  name_th: string;
  provenance: SocialProvenance;
  reason?: string;
  last_synced_at?: string;
  summary: SocialPostsSummary;
  comparison?: SocialPostsCompare | null;
  posts: SocialPostCard[];
}

export interface SocialPostsResponse {
  generated_at: string;
  period: number;
  sort: 'rate' | 'volume';
  brands: SocialPostsBrand[];
}

export function fetchSocialPosts(
  period: 7 | 28,
  sort: 'rate' | 'volume',
  signal?: AbortSignal,
  brand?: string,
): Promise<SocialPostsResponse> {
  return get<SocialPostsResponse>(`/social/posts${buildQuery({ period, sort, brand })}`, signal);
}

// ── Social content performance (bead lucky-war-yftk — "ประสิทธิภาพคอนเทนต์") ──

/** Baseline (ค่าอ้างอิง) method disclosure — every verdict is page-relative. */
export interface SocialBaselineInfo {
  weeks: number;
  min_posts: number;
  strong_at: number;
  watch_below: number;
  method_th: string;
}

/** Inferred same-day campaign cluster + annotation state. */
export interface SocialCluster {
  kind: 'campaign' | 'single';
  confidence?: 'high' | 'medium' | 'low';
  status: 'inferred' | 'confirmed' | 'dismissed';
  promotion_name_th?: string;
  /** True when a post carries the confirmed fb_posts.promotion_id relation (registered campaign). */
  linked?: boolean;
  promotion_id?: string;
  name_override?: string;
  note?: string;
}

/** Verdict vs ค่าอ้างอิง. Absent → ยังมีข้อมูลไม่เพียงพอ (no verdict). */
export interface SocialVerdict {
  tier: 'strong' | 'typical' | 'watch';
  label_th: string;
  er_ratio?: number;
  reach_ratio?: number;
  baseline_scope: string;
  baseline_n: number;
  baseline_spread?: number;
}

/** One page's verdict within a mixed-page publication day. */
export interface SocialPageVerdict {
  page_external_id: string;
  brand_code: string;
  post_count: number;
  verdict?: SocialVerdict | null;
  next_action_th: string;
}

/** Strongest/weakest campaign-cluster summary for the selected period. */
export interface SocialContentSummaryItem {
  date: string;
  date_label_th: string;
  brand_code?: string;
  name_th: string;
  post_count: number;
  er_ratio: number;
  verdict: SocialVerdict | null;
  next_action_th: string;
}

export interface SocialContentSummary {
  strongest?: SocialContentSummaryItem;
  weakest?: SocialContentSummaryItem;
}

export interface SocialDayTotals {
  reach?: number; // Σ per-post — never unique people
  engagement: number | null;
  shares: number | null;
  clicks?: number;
  engagement_rate_pct?: number;
  eligible_posts: number;
  pending_posts: number;
}

/** Cumulative engagement at D0 / D+1–3 / D+4–7 from nightly snapshots. */
export interface SocialWindowCard {
  d0: number;
  d1_3: number;
  d4_7: number;
  complete: boolean;
}

export interface SocialContentPost {
  id: string;
  external_id: string;
  created_at_ict: string; // "13:05"
  message_excerpt: string;
  media_type: 'photo' | 'video' | 'other' | '';
  /** CDN preview frame extracted from stored attachments (photos AND videos); '' for text-only. */
  thumbnail_url?: string;
  permalink_url: string;
  brand_code: string;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  clicks?: number;
  reach?: number;
  engagement_rate_pct?: number;
  mature: boolean; // false → รอข้อมูลนิ่ง
  tier?: 'strong' | 'typical' | 'watch';
  promo_status?: string;
  comments_stats?: SocialPostComments;
}

/** Association ESTIMATE (สหสัมพันธ์ ไม่ใช่หลักฐานสาเหตุ). */
export interface SocialSalesPanel {
  window_start: string;
  window_end: string;
  net_total: number;
  baseline: number;
  delta_pct?: number;
  comparison_available: boolean;
  reason_th?: string;
  window_inferred: boolean;
  window_label_th: string;
  /** Present only for confirmed-relation campaigns: the worker's campaign-level result (kvd7). */
  campaign_level?: boolean;
  campaign_lift_pct?: number;
  campaign_lift_ci_low?: number;
  campaign_lift_ci_high?: number;
  campaign_directional?: boolean;
  campaign_method_active?: boolean;
  campaign_metrics_computed_at?: string;
}

export interface SocialDayGroupCard {
  date: string; // ICT YYYY-MM-DD
  date_label_th: string; // "17 ส.ค."
  weekday_th: string;
  post_count: number;
  cluster: SocialCluster;
  context_tags: string[];
  verdict?: SocialVerdict | null;
  page_verdicts?: SocialPageVerdict[];
  next_action_th: string;
  totals: SocialDayTotals;
  window?: SocialWindowCard | null;
  posts: SocialContentPost[];
  comments?: SocialPostComments | null;
  diagnostics: string[];
  sales?: SocialSalesPanel | null;
}

export interface SocialContentResponse {
  generated_at: string;
  period_days: number;
  provenance: SocialProvenance;
  reason?: string;
  last_synced_at?: string;
  baseline: SocialBaselineInfo;
  summary: SocialContentSummary | null;
  days: SocialDayGroupCard[];
}

export function fetchSocialContentPerformance(
  days: 7 | 28 = 28,
  signal?: AbortSignal,
  brand?: string,
): Promise<SocialContentResponse> {
  return get<SocialContentResponse>(`/social/content-performance${buildQuery({ days, brand })}`, signal);
}

/** Cluster annotation body (admin-gated server-side). */
export interface SocialClusterAnnotation {
  date: string; // ICT YYYY-MM-DD
  status?: 'inferred' | 'confirmed' | 'dismissed';
  name_override?: string;
  note?: string;
}

export function annotateSocialCluster(body: SocialClusterAnnotation): Promise<{ date: string; status: string }> {
  return patchJSON(`/social/content-performance/cluster`, body);
}

// ── Competitive analysis (bead lucky-war-1p1u) ──────────────────────────────
export type CompetitiveKind = 'current-trend' | 'monthly';
export type CompetitiveState = 'fresh' | 'partial' | 'sparse' | 'unavailable';
export type CompetitiveCoverageState = 'complete' | 'partial' | 'sparse' | 'unavailable';
export type CompetitiveConfidence = 'low' | 'medium' | 'high';

export interface CompetitiveClaim { th_text: string; evidence_ids: string[]; confidence?: CompetitiveConfidence }
export interface CompetitiveBrandReport {
  brand_id: string; assessability: 'assessable' | 'low_confidence' | 'observations_only'; observed_post_count: number;
  account_type_mix: { person: number; brand_official: number; influencer_media: number; unknown: number };
  facts: CompetitiveClaim[]; interpretations: CompetitiveClaim[]; promotions_seen: CompetitiveClaim[]; occasions_seen: CompetitiveClaim[];
}
export interface CompetitiveCoverage { brands: { brand_id: string; state: CompetitiveCoverageState; cells_successful: number; cells_total: number }[]; missing_query_cells: string[] }
export interface CompetitivePayload {
  meta: { period_start: string; period_end: string; generated_at: string; sample_size: number; limitations: string[]; source?: 'x' | 'facebook'; [key: string]: unknown };
  coverage: CompetitiveCoverage; brands: CompetitiveBrandReport[]; next_actions: CompetitiveClaim[];
  executive_summary?: { th_text: string; confidence: CompetitiveConfidence; evidence_ids: string[] } | null;
  comparison?: { basis_th: string; pairs: { brand_a: string; brand_b: string; observed_share_pct_a: number; coverage_state_a: string; coverage_state_b: string }[] } | null;
  month_over_month?: { prev_period_start: string; prev_period_end: string; equal_coverage_brands: string[]; changes: (CompetitiveClaim & { brand_id: string; direction: 'up' | 'down' | 'flat' })[] } | null;
}
export interface CompetitiveReport { state: CompetitiveState; id: string; payload: CompetitivePayload; freshness: { generated_at_utc: string; generated_at_bkk: string; age_hours: number } }
export interface CompetitiveLatestResponse { state: CompetitiveState; report: CompetitiveReport | null }
export interface CompetitiveReportListItem { state: CompetitiveState; id: string; report_kind: string; brand_set?: string; period_start: string; period_end: string; generated_at_utc: string; generated_at_bkk: string; sample_size: number; limitations: string[]; coverage: { brand_id: string; state: CompetitiveCoverageState }[]; freshness: { generated_at_utc: string; generated_at_bkk: string; age_hours: number } }
export interface CompetitiveReportsResponse { state: CompetitiveState; items: CompetitiveReportListItem[]; page: number; perPage: number; totalItems: number; totalPages: number }
export type CompetitiveAccountType = 'person' | 'brand_official' | 'influencer_media' | 'unknown';
export interface CompetitiveEvidence { id: string; canonical_url: string; excerpt: string; summary_th: string; author_handle: string; account_type: CompetitiveAccountType; brand_id: string; topic_id: string; state: 'valid' | 'validation_failed' | 'deleted' | 'inaccessible'; posted_at_utc: string; posted_at_bkk: string; collection_run_id: string; cell_id: string }
export interface CompetitiveEvidenceResponse { state: CompetitiveState; items: CompetitiveEvidence[]; total: number; page: number; perPage: number; totalItems: number; totalPages: number; missing_evidence_ids: string[] }

export function fetchCompetitiveLatest(kind: CompetitiveKind = 'current-trend', set?: 'suki' | 'bbq', signal?: AbortSignal): Promise<CompetitiveLatestResponse> {
  return get<CompetitiveLatestResponse>(`/competitive/latest${buildQuery({ kind, set })}`, signal);
}
export function fetchCompetitiveReport(id: string, signal?: AbortSignal): Promise<CompetitiveReport> {
  return get<CompetitiveReport>(`/competitive/reports/${encodeURIComponent(id)}`, signal);
}
export function fetchCompetitiveReports(kind: CompetitiveKind, page = 1, perPage = 10, set?: 'suki' | 'bbq', signal?: AbortSignal): Promise<CompetitiveReportsResponse> {
  return get<CompetitiveReportsResponse>(`/competitive/reports${buildQuery({ kind, page, perPage, set })}`, signal);
}
export function fetchCompetitiveEvidence(reportId: string, evidenceIds?: string[], signal?: AbortSignal): Promise<CompetitiveEvidenceResponse> {
  // The endpoint paginates the report's evidence; the optional IDs are used by
  // the drawer to avoid fetching the whole report when a claim has few sources.
  const path = `/competitive/reports/${encodeURIComponent(reportId)}/evidence${buildQuery({ page: 1, perPage: 200 })}`;
  return get<CompetitiveEvidenceResponse>(path, signal).then((response) => evidenceIds?.length
    ? { ...response, items: response.items.filter((item) => evidenceIds.includes(item.id)) }
    : response);
}

// ── Promo admin: promotions ↔ content mapper (bead lucky-war-h6zm) ──────────
// Admin-gated surface per docs/plans/SOCIAL-DEMAND-ATTRIBUTION.md §3.

export interface PromoAdminPromotion {
  id: string;
  name_th: string;
  description_th: string;
  item_codes: string[];
  vc_codes: string[];
  start_date: string;
  end_date: string;
  price_adult: number;
  price_child: number;
  brand_scope: string[];
  branch_scope?: string[];
  branch_scope_names?: string[];
  status: string;
  source_post_id: string;
  linked_post_count: number;
}

export interface PromoAdminPromotionsResponse {
  promotions: PromoAdminPromotion[];
}

export interface PromoAdminPost {
  id: string;
  external_id: string;
  page_external_id: string;
  brand_code: string;
  message: string;
  created_at: string;
  permalink_url: string;
  promotion_id: string;
}

export interface PromoAdminPostsResponse {
  posts: PromoAdminPost[];
}

export interface PromoAdminUpsertPayload {
  name_th: string;
  description_th?: string;
  item_codes?: string[];
  vc_codes?: string[];
  start_date: string;
  end_date: string;
  price_adult?: number;
  price_child?: number;
  brand_scope?: string[];
  branch_scope?: string[];
  status?: string;
}

export interface PromoAdminLinkPayload {
  post_id: string;
  promotion_id?: string; // optional for link callers that have no promotion
}

export interface PromoAdminLinkResponse {
  post_id: string;
  promotion_id: string;
  source_post_id: string;
}

export function fetchPromoAdminPromotions(signal?: AbortSignal): Promise<PromoAdminPromotionsResponse> {
  return get<PromoAdminPromotionsResponse>('/promo-admin/promotions', signal);
}

export function createPromoAdminPromotion(payload: PromoAdminUpsertPayload): Promise<PromoAdminPromotion> {
  return postJSON<PromoAdminPromotion>('/promo-admin/promotions', payload);
}

export function updatePromoAdminPromotion(id: string, payload: PromoAdminUpsertPayload): Promise<PromoAdminPromotion> {
  return patchJSON<PromoAdminPromotion>(`/promo-admin/promotions/${encodeURIComponent(id)}`, payload);
}

export function fetchPromoAdminPosts(
  params: { from?: string; to?: string; brand?: string; q?: string; limit?: number },
  signal?: AbortSignal,
): Promise<PromoAdminPostsResponse> {
  const query = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '' && v !== null)
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');
  return get<PromoAdminPostsResponse>(`/promo-admin/posts${query ? `?${query}` : ''}`, signal);
}

export function linkPromoAdminPost(payload: PromoAdminLinkPayload): Promise<PromoAdminLinkResponse> {
  return postJSON<PromoAdminLinkResponse>('/promo-admin/link', payload);
}

export function unlinkPromoAdminPost(postId: string, promotionId = ''): Promise<PromoAdminLinkResponse> {
  return postJSON<PromoAdminLinkResponse>('/promo-admin/unlink', {
    post_id: postId,
    promotion_id: promotionId,
  });
}

// ── Admin user management (bead lucky-war-1t4r.1) ────────────────────────────
// Backs the admin section on /ops: approve/revoke + role assignment by
// approved role=admin LINE users. Server gate: /api/admin/users/* (superuser
// or approved admin); self-actions and last-admin demotion return 4xx.

export interface AdminUserRow {
  id: string;
  display_name: string;
  picture_url: string;
  email: string;
  line_user_id: string;
  role: string;     // viewer | admin
  approved: boolean;
  created: string;
  last_visit_at: string | null;
}

export interface AdminUsersResponse {
  users: AdminUserRow[];
}

/**
 * The /api/admin/* routes live OUTSIDE the /api/dashboard base that get()/
 * postJSON()/patchJSON()/deleteJSON() prepend, so this group issues raw
 * fetches at absolute paths. Same auth-error interception as get().
 */
async function adminRequest<T>(
  path: string,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  payload?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: payload !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: payload !== undefined ? JSON.stringify(payload) : undefined,
    signal,
  });
  if (res.ok) return res.json() as Promise<T>;
  let body: ErrorBody | null = null;
  try {
    body = (await res.json()) as ErrorBody;
  } catch {
    throw new ApiError(res.status, `ไม่สามารถเชื่อมต่อได้ (${res.status})`, null);
  }
  await handleAuthError(res.status, body);
  throw new ApiError(
    res.status,
    body?.error === 'forbidden' ? 'ต้องการสิทธิ์ผู้ดูแล (admin)' : `ไม่สามารถเชื่อมต่อได้ (${res.status})`,
    body?.error || null,
  );
}

export function fetchAdminUsers(signal?: AbortSignal): Promise<AdminUsersResponse> {
  return adminRequest<AdminUsersResponse>('/api/admin/users', 'GET', undefined, signal);
}

export function approveAdminUser(id: string): Promise<{ id: string; approved: boolean }> {
  return adminRequest(`/api/admin/users/${encodeURIComponent(id)}/approve`, 'POST', {});
}

export function revokeAdminUser(id: string): Promise<{ id: string; approved: boolean }> {
  return adminRequest(`/api/admin/users/${encodeURIComponent(id)}/revoke`, 'POST', {});
}

export function setAdminUserRole(id: string, role: 'viewer' | 'admin'): Promise<{ id: string; role: string }> {
  return adminRequest(`/api/admin/users/${encodeURIComponent(id)}/role`, 'PATCH', { role });
}

// ── Manager contacts CRUD (bead lucky-war-1t4r.1) ────────────────────────────
// Store-manager LINE userIds per branch for the (deferred-delivery) weekly
// push. Friend status is NOT queryable server-side — the UI shows a hint.

export interface ManagerContactRow {
  id: string;
  branch_id: string;
  branch_name: string;
  line_user_id: string;
  display_name: string;
  role: string;   // primary | backup
  active: boolean;
  notes: string;
  created: string;
}

export interface ManagerContactsResponse {
  contacts: ManagerContactRow[];
}

export interface ManagerContactPayload {
  branch_id?: string;
  line_user_id?: string;
  display_name?: string;
  role?: string;
  active?: boolean;
  notes?: string;
}

export function fetchManagerContacts(branchId?: string, signal?: AbortSignal): Promise<ManagerContactsResponse> {
  const q = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : '';
  return adminRequest<ManagerContactsResponse>(`/api/admin/manager-contacts${q}`, 'GET', undefined, signal);
}

export function createManagerContact(payload: ManagerContactPayload): Promise<ManagerContactRow> {
  return adminRequest('/api/admin/manager-contacts', 'POST', payload);
}

export function updateManagerContact(id: string, payload: ManagerContactPayload): Promise<ManagerContactRow> {
  return adminRequest(`/api/admin/manager-contacts/${encodeURIComponent(id)}`, 'PATCH', payload);
}

export function deleteManagerContact(id: string): Promise<{ id: string; deleted: boolean }> {
  return adminRequest(`/api/admin/manager-contacts/${encodeURIComponent(id)}`, 'DELETE');
}

// ── Review full-text search (bead lucky-war-1t4r.3) ──────────────────────────
// Chain-wide LIKE-based search over verbatim review text (FTS5 rejected —
// Thai is not space-delimited; see server review_search.go).

export interface ReviewSearchItem {
  review_id: string;
  branch_id: string;
  branch_name: string;
  brand_code?: string;
  source_id?: string;
  text: string;
  rating: number | null;
  sentiment: string;
  topics: string[];
  posted_at: string;
  source_url?: string;
}

export interface ReviewSearchResponse {
  items: ReviewSearchItem[];
  total: number;
  page: number;
  perPage: number;
  query: string;
  terms: string[];
  days: number;
}

export function fetchReviewSearch(
  params: { q: string; days?: number; sentiment?: string; branch?: string; brand?: string; page?: number; perPage?: number },
  signal?: AbortSignal,
): Promise<ReviewSearchResponse> {
  const query = buildQuery({
    q: params.q,
    days: params.days,
    sentiment: params.sentiment && params.sentiment !== 'all' ? params.sentiment : undefined,
    branch: params.branch,
    brand: params.brand,
    page: params.page,
    perPage: params.perPage,
  });
  return get<ReviewSearchResponse>(`/reviews/search${query}`, signal);
}

// ── Branch-vs-branch comparison (bead lucky-war-1t4r.2) ──────────────────────
// Side-by-side KPI grid for /compare?ids=A,B,C. Same aggregation formulas as
// the branch list + region compare so numbers cross-check.

export interface BranchCompareTopic {
  topic: string;
  display_name: string;
  neg_count: number;
}

export interface BranchCompareRow {
  branch_id: string;
  branch_name: string;
  brand_code: string;
  region_name: string;
  rating: number | null;
  review_count_total: number | null;
  review_count_30d: number;
  negative_pct: number | null;
  is_experimental: boolean;
  fb_comment_count: number;
  fb_negative_pct: number | null;
  sales_net_total: number | null;
  health_status: string;
  last_success_at: string | null;
  top_topics: BranchCompareTopic[];
}

export interface BranchCompareMetricStatus {
  fb: 'ok' | 'unavailable';
  sales: 'ok' | 'unavailable';
  topics: 'ok' | 'unavailable';
}

export interface BranchCompareResponse {
  days: number;
  sales_period: string;
  metric_status: BranchCompareMetricStatus;
  branches: BranchCompareRow[];
}

export function fetchBranchCompare(ids: string[], signal?: AbortSignal): Promise<BranchCompareResponse> {
  return get<BranchCompareResponse>(`/compare?ids=${ids.map(encodeURIComponent).join(',')}`, signal);
}
